import { Component, ElementRef, ViewChild } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { environment } from "@environment";
import { Point, CdkDrag } from "@angular/cdk/drag-drop";

import { GtmService } from "@app/core/services/analytics/gtm.service";
import { LoginService } from "@app/core/services/login/login.service";
import { StorageService } from "@app/core/services/util/storage.service";
import { CustomerApiService } from "@app/core/http/customer-api.service";
import { ChatbotService } from "@app/core/services/chatbot/chatbot.service";
import { SessionStorageService } from "@app/core/services/util/session-storage.service";

import { TicketApiResponse } from "@app/shared/models/api-response/ticket-details.api-response";
import { BaseComponent } from "../base/base.component";

@Component({
    selector: "app-chat-bubble",
    templateUrl: "./chat-bubble.component.html",
    styleUrls: ["./chat-bubble.component.scss"],
    standalone: true,
    imports: [CdkDrag]
})
export class ChatBubbleComponent extends BaseComponent {

	ticket: any;
    showDropZone = false;
    showChatBubble = false;
    DEPLOY_URL = environment.DEPLOY_URL;

    @ViewChild("dropZone", { static: false }) dropZone: ElementRef;

    constructor(
        private gtmService: GtmService,
        private loginService: LoginService,
        private chatbotService: ChatbotService,
        private storageService: StorageService,
        private customerApiService: CustomerApiService,
        private sessionStorage: SessionStorageService
    ) {
		super();
	}

    ngOnInitBrowser(): void {

        this.loginService.userLoginSuccess
            .subscribe(() => {
                this.getTicketDetails();
            });

        this.loginService.userLogoutSuccess
            .subscribe(() => {
                this.showChatBubble = false;
            });

        this.chatbotService.isChatBotClosed
            .subscribe(() => {
                this.getTicketDetails();
            });

        const isLoggedIn = this.storageService.get("session-id") && this.storageService.get("phone_no");

        if (!isLoggedIn) {
            return;
        }

        this.getTicketDetails();
    }

    getTicketDetails() {
        lastValueFrom(this.customerApiService.getTicketDetails())
            .then((response: TicketApiResponse) => {
                if (response?.tickets.length) {
                    this.initChatBubble(response);
                } else {    
                    this.showChatBubble = false;
                }
            }).catch((err) => {
                console.log(err);
                this.showChatBubble = false;
            });
    }

    async initChatBubble(response: TicketApiResponse) {
        await this.chatbotService.chatConfig()
            .then(() => {
                this.ticket = response.tickets[0];
                this.checkForFaqDetails(response.tickets[0].faq_id);
            }).catch((err) => {
                console.log(err);
                this.showChatBubble = false;
            });
    }

    checkForFaqDetails(faq_id: any) {
        const faqDetails = this.sessionStorage.get("faq_details") || {};
        const faq = faqDetails[faq_id];

        if (!faq) {
            this.showChatBubble = false;
            this.gtmService.logWebAnalyticsEvent("chat_bot", "Faq Details Not Present, ID: " + faq_id);
        } else {
            this.showChatBubble = true;
        }
    }

    dragStart() {
        this.showDropZone = true;
        this.gtmService.logWebAnalyticsEvent("chat_bot", "Drag Start");
    }

    dragReleased(e) {
        const dropZoneElement: HTMLElement = this.dropZone.nativeElement;
        const dragRect: DOMRect = dropZoneElement.getBoundingClientRect();
        const dropRect: DOMRect = e.source.element.nativeElement.getBoundingClientRect();

        const intersects = !(
            dragRect.right < dropRect.left ||
            dragRect.left > dropRect.right ||
            dragRect.bottom < dropRect.top ||
            dragRect.top > dropRect.bottom
        );

        if (intersects) {
            this.showChatBubble = false;
            this.gtmService.logWebAnalyticsEvent("chat_bot", "Drag Released: DropZone");
        } else {
            this.gtmService.logWebAnalyticsEvent("chat_bot", "Drag Released: OutSize DropZone");
        }
        this.showDropZone = false;
    }

    dragConstrainPoint(point: Point): Point {
        const { x, y } = point;
        const minX = 0;
        const minY = 0;
        const maxX = window.innerWidth - 50;
        const maxY = window.innerHeight - 50;
    
        const constrainedX = Math.min(maxX, Math.max(minX, x));
        const constrainedY = Math.min(maxY, Math.max(minY, y));
    
        return { x: constrainedX, y: constrainedY };
    };
    
    openChat(e) {
        this.gtmService.logWebAnalyticsEvent("chat_bot", "Open ChatBot");

        this.chatbotService.addMessageListener();

        const { faq_id, tracking_id } = this.ticket;
        const faqDetails = this.sessionStorage.get("faq_details") || {};
        const faq = faqDetails[faq_id];

        const { question, context } = faq;
        const ymAuthenticationToken = `$${this.storageService.get('customer_id')}$${tracking_id}$${faq_id}$`;

        const config = {
            "ymAuthenticationToken": ymAuthenticationToken,
            "payload":
            {
                faq_id,
                tracking_id,
                query: question,
                journey: context,
                user_name: this.storageService.get("name"),
                email_id: this.storageService.get("email"),
                phone_no: this.storageService.get("phone_no"),
                auth_token: this.storageService.get("session-id")
            }
        };

        if (window && window['YellowMessengerPlugin']) {
            window['YellowMessengerPlugin'].init(
                config
            );
            window['YellowMessengerPlugin'].openBot();
        }
        e.stopPropagation();
    }
}
